import { useState } from 'react';
import ConfirmActionDialog from '../../../components/admin/ConfirmActionDialog';
import InlineNotice from '../../../components/admin/InlineNotice';
import { useBlockIp } from '../../../hooks/admin/useAdminSecurity';
import { EXPIRY_PRESETS, expiryPresetToHours, type ExpiryPreset } from '../../../lib/adminFilters';
import { getApiErrorCode, getApiErrorMessage } from '../../../lib/apiError';

interface BlockIpDialogProps {
  ip: string | null;
  /** The admin's own IP as reported by the overview; used to warn before self-lockout. */
  selfIp?: string | null;
  initialReason?: string;
  onClose: () => void;
  onBlocked?: (ip: string) => void;
}

/**
 * Block-an-IP confirmation shared by the overview table, the traffic drilldown
 * and the blocked-IPs tab. Handles the API's SELF_IP guard: the first attempt
 * never sends `force`; if the API refuses, an explicit second confirmation is
 * required before retrying with `force: true`.
 *
 * Render with `key={ip}` so expiry / force stage / mutation state reset per IP.
 */
export default function BlockIpDialog({ ip, selfIp, initialReason = '', onClose, onBlocked }: BlockIpDialogProps) {
  const [expiry, setExpiry] = useState<ExpiryPreset>('24h');
  const [forceStage, setForceStage] = useState(false);
  const mutation = useBlockIp();

  const isSelf = Boolean(ip && selfIp && ip === selfIp);
  const apiCode = mutation.isError ? getApiErrorCode(mutation.error) : null;
  const selfRefused = apiCode === 'SELF_IP';

  const confirm = async (reason: string) => {
    if (!ip) return;
    if (selfRefused && !forceStage) {
      // First refusal: switch the dialog into the explicit "block anyway" stage.
      setForceStage(true);
      mutation.reset();
      return;
    }
    try {
      await mutation.mutateAsync({
        ip,
        reason: reason || undefined,
        expiresInHours: expiryPresetToHours(expiry),
        force: forceStage ? true : undefined,
      });
      onBlocked?.(ip);
      onClose();
    } catch {
      /* shown inline */
    }
  };

  const error = mutation.isError && !selfRefused ? getApiErrorMessage(mutation.error, 'Failed to block IP') : null;

  return (
    <ConfirmActionDialog
      isOpen={Boolean(ip)}
      onClose={onClose}
      title={forceStage ? `Block your own IP ${ip}?` : `Block IP ${ip}`}
      tone="danger"
      description={
        forceStage
          ? 'This is the IP address you are connecting from. Blocking it will lock you out of the admin as soon as it takes effect. Recovery: delete the row from "BlockedIp" in the database.'
          : 'All requests from this address get 403 IP_BLOCKED until the block expires or is removed. Blocks apply within about a minute on all API instances.'
      }
      confirmLabel={forceStage ? 'Block my own IP anyway' : selfRefused ? 'Continue' : 'Block IP'}
      optionalReason
      initialReason={initialReason}
      reasonLabel="Reason (optional)"
      reasonPlaceholder="e.g. 5,000 requests to /api/kols in 10 minutes"
      isPending={mutation.isPending}
      error={error}
      onConfirm={confirm}
    >
      {(isSelf || selfRefused) && !forceStage && (
        <InlineNotice tone="warn">
          {selfRefused
            ? 'The API refused: this is your own IP address. Continue only if you really want to block yourself.'
            : 'Heads up: this looks like your own IP address. The API will refuse unless you explicitly force it.'}
        </InlineNotice>
      )}
      <div>
        <label className="mb-1 block text-sm font-medium text-glass-text">Expires</label>
        <select
          value={expiry}
          onChange={(e) => setExpiry(e.target.value as ExpiryPreset)}
          className="glass-input w-full px-3 py-2 text-sm"
          disabled={mutation.isPending}
        >
          {EXPIRY_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
    </ConfirmActionDialog>
  );
}
